// Copyright (c) 2023 WiseTime. All rights reserved.

import { DaySummary, ListDaySummariesResponse } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import {
  Leave,
  LeaveAmount,
  MemberExpectation
} from "../../../generated/legebill/site/postexpectation/v4/post_expectation_pb"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  convertTimelineDateToPbDate,
  DateRange,
  FormattedTimeStats,
  formatTimeStats,
  getTimelineDatesInRange,
  TimelineDate,
} from "../dateTime"
import { equals } from "ramda"
import { RootState } from "../../../app/store"
import { emptyArray } from "../../../lib/utils"
import { getTileHoursCount } from "../action-pane/tiles/tiles"
import { selectCurrentSiteId } from "../../user-context/slice"
import { selectActiveDate } from "./timeline"

export type DaySummaries = {
  [date: string]: DaySummary.AsObject,
}

export type DaySummariesResult = {
  range: DateRange,
  response: ListDaySummariesResponse.AsObject,
}

export type MemberExpectationsCache = {
  [siteId: string]: MemberExpectation.AsObject[],
}

type MemberExpectationsCachePayload = {
  siteId: string,
  memberExpectations: MemberExpectation.AsObject[],
}

export type TimelineStatsState = {
  daySummaries: DaySummaries,
  leaveList: Leave.AsObject[],
  memberExpectationsCache: MemberExpectationsCache,
}

export const initialState: TimelineStatsState = {
  daySummaries: {},
  leaveList: [],
  memberExpectationsCache: {},
}

export const slice = createSlice({
  name: "timeline-stats",
  initialState,
  reducers: {
    setDaySummaries: (state, action: PayloadAction<DaySummariesResult>): void => {
      const dayRange = getTimelineDatesInRange(action.payload.range.startDateLocal, action.payload.range.endDateLocal)
      dayRange.forEach(date => {
        const daySummary = action.payload.response.daySummariesList
          .find(daySummary => daySummary.date == date.toString())
        if (daySummary) {
          state.daySummaries[date.toString()] = daySummary
        } else {
          delete state.daySummaries[date.toString()]
        }
      })
    },
    setLeaveList: (state, action: PayloadAction<Leave.AsObject[]>) => {
      state.leaveList = action.payload
    },
    addLeave: (state, action: PayloadAction<Leave.AsObject>) => {
      const existingLeaveIndex = state.leaveList.findIndex(l => equals(l.date, action.payload.date))
      if (existingLeaveIndex !== -1) {
        state.leaveList[existingLeaveIndex] = action.payload
      } else {
        state.leaveList.push(action.payload)
      }
    },
    deleteLeave: (state, action: PayloadAction<string>) => {
      const index = state.leaveList.findIndex(l => l.id === action.payload)
      if (index !== -1) {
        state.leaveList.splice(index, 1)
      }
    },
    setSiteMemberExpectations: (state, action: PayloadAction<MemberExpectationsCachePayload>) => {
      state.memberExpectationsCache[action.payload.siteId] = action.payload.memberExpectations
    },
    updateSiteMemberExpectations: (state, action: PayloadAction<MemberExpectationsCachePayload>) => {
      action.payload.memberExpectations.forEach(memberExpectation => {
        const cachedDayExpectationsList = state.memberExpectationsCache[action.payload.siteId]
          ?.find(me => me.postExpectationId === memberExpectation.postExpectationId)
          ?.dayExpectationsList
        if (cachedDayExpectationsList === undefined) {
          console.warn("Unable to find day expectations list in cache for update.")
          return
        }
        memberExpectation.dayExpectationsList.forEach(dayExpectation => {
          const index = cachedDayExpectationsList.findIndex(cachedDayExpectation => (
            cachedDayExpectation.date?.day === dayExpectation.date?.day
            && cachedDayExpectation.date?.month === dayExpectation.date?.month
            && cachedDayExpectation.date?.year === dayExpectation.date?.year
          ))
          if (index !== -1) {
            cachedDayExpectationsList[index] = dayExpectation
          }
        })
      })
    },
  }
})

export const {
  setDaySummaries,
  setLeaveList,
  addLeave,
  deleteLeave,
  setSiteMemberExpectations,
  updateSiteMemberExpectations,
} = slice.actions

export const selectDaySummaries = (state: RootState): DaySummaries =>
  state.timelineStats.daySummaries

export const selectDaySummary = () => createSelector(
  selectDaySummaries,
  (_: RootState, timelineDate: TimelineDate) => timelineDate,
  (
    daySummaries,
    timelineDate,
  ): DaySummary.AsObject | undefined => daySummaries[timelineDate],
)

export const selectLeave = () => createSelector(
  (state: RootState) => state.timelineStats.leaveList,
  (_: RootState, timelineDate: TimelineDate) => timelineDate,
  (
    leavesList,
    timelineDate,
  ): Leave.AsObject | undefined => leavesList.find((leave => (
    leave.date !== undefined
    && leave.amount !== LeaveAmount.LEAVE_AMOUNT_UNSPECIFIED
    && equals(leave.date, convertTimelineDateToPbDate(timelineDate).toObject())
  ))),
)

const selectMemberExpectationsCache = (state: RootState): MemberExpectationsCache =>
  state.timelineStats.memberExpectationsCache

export const selectMemberExpectations = (state: RootState): MemberExpectation.AsObject[] =>
  selectMemberExpectationsCache(state)[selectCurrentSiteId(state) ?? ""] ?? emptyArray

export const selectTileHoursCount = createSelector(
  selectCurrentSiteId,
  selectMemberExpectationsCache,
  (_: RootState, start: TimelineDate, end: TimelineDate): [TimelineDate, TimelineDate] => [start, end],
  (siteId, memberExpectations, [start, end]) => getTileHoursCount(
    memberExpectations[siteId ?? ""] ?? emptyArray,
    start,
    end
  )
)

export const selectTimeStats = createSelector(
  selectActiveDate,
  selectDaySummaries,
  (activeDate, daySummaries): FormattedTimeStats => formatTimeStats(daySummaries[activeDate])
)

export default slice.reducer
